/**
 * TanStack Query hooks for the user-management API (plan 01-05 backend).
 *
 * GET endpoint is shared across Content Admin / Super-admin / HR Manager.
 * Mutations all 403 from anyone except Content Admin (the backend is the source
 * of truth — the UI just hides the affordances for read-only roles).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { Lang, Role, UserListPage, UserOut } from "@/types/api";

export interface UserListParams {
  search?: string;
  role?: Role;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}

export interface UserCreateBody {
  username: string;
  role: Role;
  preferred_lang: Lang;
  initial_password: string;
}

export interface UserUpdateBody {
  role?: Role;
  preferred_lang?: Lang;
  is_active?: boolean;
}

export interface ResetPasswordBody {
  new_initial_password: string;
}

function buildQuery(params: UserListParams): string {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.role) qs.set("role", params.role);
  if (params.is_active !== undefined) qs.set("is_active", String(params.is_active));
  qs.set("limit", String(params.limit ?? 50));
  qs.set("offset", String(params.offset ?? 0));
  return qs.toString();
}

export function useUsers(params: UserListParams = {}) {
  const qs = buildQuery(params);
  return useQuery({
    queryKey: ["users", qs],
    queryFn: () => apiFetch<UserListPage>(`/api/admin/users?${qs}`),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateBody) =>
      apiFetch<UserOut>("/api/admin/users", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UserUpdateBody }) =>
      apiFetch<UserOut>(`/api/admin/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ResetPasswordBody }) =>
      apiFetch<void>(`/api/admin/users/${id}/reset-password`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}
